import { useCallback, useEffect, useState } from "react";
import validarIp from "../../../../../../utils/validadorIp";

// Shape default do formulário de circuito
const DEFAUL_FORM = {
  id: "",
  clientServiceId: "",
  blockIP: "",
  vlanId: "",
  companySideIPv4: "",
  companySideIPv6: "",
  clientIPv4: "",
  clientIPv6: "",
  router: "",
  deliveryEquipment: "",
  clientEquipment: "",
  observations: "",
  lastMilePartner: {
    partnerEnabled: false,
    partnerName: "",
    partnerContact: "",
    escalationContacts: [
      {
        id: "",
        name: "",
        role: "",
        phone: "",
        escalationTime: 0,
        clientCircuitId: "",
      },
    ],
  },
  endClient: {
    endClientEnabled: false,
    endClientName: "",
  },
  speed: {
    download: "",
    upload: "",
  },
  serviceTypes: [],
  deliveryMedium: [],
  serial: "",
  ipPublico: "",
};

// Hook que concentra o estado principal do formulário de circuito (prevData/
// formData/errors) e os handlers/validações que operam sobre ele. Extraído de
// circuito/index.js (Task 4 da decomposição).
export default function useCircuitoForm({ data, delivery, serviceId, setChanged }) {
  // Estado principal do formulário
  const [prevData, setPrevData] = useState(
    JSON.parse(
      JSON.stringify({
        ...DEFAUL_FORM,
        ...delivery,
        ...data,
        clientServiceId: serviceId,
      }),
    ),
  );

  const [formData, setFormData] = useState(
    JSON.parse(
      JSON.stringify({
        ...DEFAUL_FORM,
        ...delivery,
        ...data,
        clientServiceId: serviceId,
      }),
    ),
  );

  // Validação
  const [errors, setErrors] = useState({
    blockIP: false,
    companySideIPv4: false,
    companySideIPv6: false,
    clientIPv4: false,
    clientIPv6: false,
    serial: false,
    ipPublico: false,
  });

  // Serial da ONU: a busca (deduplicada por serviço) vive em serviceItem.js e chega
  // aqui via prop `data.serial` — como formData só lê `data` na montagem (não resincroniza
  // sozinho), este efeito cobre o caso do serial chegar DEPOIS que este circuito já montou.
  // Só preenche se formData ainda não tem serial, para nunca sobrescrever o que o usuário digitou.
  useEffect(() => {
    if (!formData?.serial && data?.serial) {
      handleChange({ target: { name: "serial", value: data.serial } });
      setPrevData((prev) => ({ ...prev, serial: data.serial }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data?.serial]);

  // Manipuladores de mudança genéricos
  const handleChange = (e) => {
    setChanged(true);
    const { name, value, type, checked } = e.target;
    if (type === "checkbox") {
      const [objName, indexStr] = name?.split(".") ?? [];
      const index = Number(indexStr);
      if (!!objName && !!indexStr) {
        setFormData((prev) => {
          const newArray = [...prev[objName]];
          // newArray[index].value = `${checked}`;
          newArray[index].value = checked;
          return {
            ...prev,
            [objName]: newArray,
          };
        });
      }
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
    if (
      name === "clientIPv4" ||
      name === "clientIPv6" ||
      name === "companySideIPv6" ||
      name === "companySideIPv4" ||
      name === "blockIP" ||
      name === "ipPublico"
    ) {
      validateBlocoAndIp({ name, value });
    }
  };

  const handleNestedChange = (parent, field, value) => {
    setChanged(true);
    setFormData((prev) => ({
      ...prev,
      [parent]: {
        ...prev[parent],
        [field]: value,
      },
    }));
  };

  // Recebe { name, value } para validar o valor recém-digitado (o estado ainda
  // não foi aplicado quando handleChange chama); sem override, valida o estado atual.
  // useCallback: identidade estável entre renders, consumida por handleSave (useCircuitoSave)
  const validateBlocoAndIp = useCallback((override) => {
    const campoAlterado = override?.name;
    const dados = campoAlterado
      ? { ...formData, [campoAlterado]: override?.value }
      : formData;

    const newErrors = {
      blockIP: !!dados?.blockIP && !validarIp(dados?.blockIP, "justBloco"),
      companySideIPv4:
        !!dados?.companySideIPv4 &&
        !validarIp(dados?.companySideIPv4, "justIpv4"),
      clientIPv4:
        !!dados?.clientIPv4 && !validarIp(dados?.clientIPv4, "justIpv4"),
      companySideIPv6:
        !!dados?.companySideIPv6 &&
        !validarIp(dados?.companySideIPv6, "justIpv6"),
      clientIPv6:
        !!dados?.clientIPv6 && !validarIp(dados?.clientIPv6, "justIpv6"),
      ipPublico:
        !!dados?.ipPublico &&
        !validarIp(dados?.ipPublico, "justIpv4") &&
        !validarIp(dados?.ipPublico, "justBloco"),
    };

    setErrors(newErrors);
    return !Object?.values(newErrors)?.some((error) => error);
  }, [formData]);

  // Reverte formData para o último estado salvo (prevData); handleReset em
  // circuito/index.js chama isso e depois reseta o diagrama separadamente.
  // useCallback: identidade estável entre renders, consumida por handleReset (circuito/index.js)
  const restaurarPrevData = useCallback(() => {
    setFormData(JSON.parse(JSON.stringify(prevData)));
  }, [prevData]);

  // Velocidade 0 é um valor válido: checagem por truthiness descartaria o zero
  const temVelocidade = (valor) =>
    valor !== null && valor !== undefined && valor !== "";

  return {
    prevData,
    setPrevData,
    formData,
    setFormData,
    errors,
    setErrors,
    handleChange,
    handleNestedChange,
    validateBlocoAndIp,
    temVelocidade,
    restaurarPrevData,
  };
}
